import { useCallback, useEffect, useRef } from 'react';

/**
 * Attach a wheel handler that can actually cancel the gesture.
 *
 * React delegates `wheel` to the root container with `passive: true`, so
 * `preventDefault()` inside an `onWheel` prop is dropped and the responsive
 * stack scrolls under a chart that is zooming. The listener has to sit on the
 * element itself with `{ passive: false }`.
 *
 * Takes a ref rather than the handler so the call site can sit above a
 * data-dependent early return while the handler itself is built after it.
 * Returns a ref callback for the element.
 */
export default function useNonPassiveWheel(handlerRef) {
  const nodeRef = useRef(null);

  const dispatch = useCallback((event) => {
    handlerRef.current?.(event);
  }, [handlerRef]);

  const detach = useCallback(() => {
    if (!nodeRef.current) return;
    nodeRef.current.removeEventListener('wheel', dispatch);
    nodeRef.current = null;
  }, [dispatch]);

  const attach = useCallback((node) => {
    detach();
    if (!node) return;
    nodeRef.current = node;
    node.addEventListener('wheel', dispatch, { passive: false });
  }, [detach, dispatch]);

  useEffect(() => detach, [detach]);

  return attach;
}
